import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getAllTasks } from "../../redux/actions/authActions";
import { Box, Typography, Paper, Grid, Divider, Button, Chip } from "@mui/material";
import { IoIosArrowBack } from "react-icons/io";
import dayjs from "dayjs";

const ViewReportTask = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const tasks = useSelector((state) => state.auth.allTasks);
  const [task, setTask] = useState(null);

  useEffect(() => {
    if (!tasks || tasks.length === 0) {
      dispatch(getAllTasks(localStorage.getItem("token")));
    }
  }, [dispatch, tasks]);

  useEffect(() => {
    if (tasks && tasks.length > 0) {
      const selectedTask = tasks.find((t) => t._id === id);
      setTask(selectedTask);
    }
  }, [tasks, id]);

  if (!task) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6">Loading task details...</Typography>
      </Box>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "Completed": return "success";
      case "InProgress": return "warning";
      case "Pending": return "info";
      default: return "default";
    }
  };

  return (
    <Box sx={{ p: 4 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" color="text.primary">
          Report Details
        </Typography>
        <Button
          variant="outlined"
          startIcon={<IoIosArrowBack />}
          onClick={() => navigate(-1)}
          sx={{ borderRadius: 10, textTransform: "none" }}
        >
          Back to Reports
        </Button>
      </Box>

      <Paper sx={{ p: 4, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
        <Grid container spacing={4}>
          {/* Header Info */}
          <Grid item xs={12} md={8}>
            <Typography variant="overline" color="text.secondary" fontWeight="bold">
              Task Name
            </Typography>
            <Typography variant="h5" fontWeight="800" sx={{ mb: 1 }}>
              {task.service?.name || task.name || "Untitled Task"}
            </Typography>
            <Chip 
              label={task.status || "Unknown"} 
              color={getStatusColor(task.status)}
              size="small"
              sx={{ fontWeight: "bold", px: 1 }}
            />
          </Grid>
          <Grid item xs={12} md={4} sx={{ textAlign: { md: "right" } }}>
            <Typography variant="overline" color="text.secondary" fontWeight="bold">
              Due Date
            </Typography>
            <Typography variant="h6" fontWeight="600">
              {task.dueDate ? dayjs(task.dueDate).format("MMMM D, YYYY") : "No Due Date"}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
          </Grid>

          {/* Client & Assignee */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight="bold">
              CLIENT INFORMATION
            </Typography>
            <Typography variant="body1" fontWeight="600">
              {task.client?.businessName || task.client?.name || "N/A"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {task.client?.email || ""}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight="bold">
              ASSIGNED TO
            </Typography>
            <Typography variant="body1" fontWeight="600">
              {task.assignees?.map(a => a.name).join(", ") || "Unassigned"}
            </Typography>
          </Grid>

          {/* Escalations */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 2, mb: 2, fontWeight: "bold" }}>
              Escalations & Remarks
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, bgcolor: "#fff1f1", borderLeft: "4px solid #ef4444", borderRadius: 1 }}>
              <Typography variant="caption" color="error" fontWeight="bold" sx={{ display: "block", mb: 1 }}>
                🚨 TEAM ESCALATION REASON
              </Typography>
              <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                {task.teamDelayReason || "No team escalation reported."}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, bgcolor: "#fff7ed", borderLeft: "4px solid #f97316", borderRadius: 1 }}>
              <Typography variant="caption" sx={{ color: "#c2410c", fontWeight: "bold", display: "block", mb: 1 }}>
                📢 CLIENT ESCALATION REASON
              </Typography>
              <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                {task.clientDelayReason || "No client escalation reported."}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, bgcolor: "#f0fdf4", borderLeft: "4px solid #166534", borderRadius: 1 }}>
              <Typography variant="caption" sx={{ color: "#166534", fontWeight: "bold", display: "block", mb: 1 }}>
                📝 TEAM REMARKS
              </Typography>
              <Typography variant="body2">
                {task.teamRemarks || "No team remarks available."}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, bgcolor: "#eff6ff", borderLeft: "4px solid #1e40af", borderRadius: 1 }}>
              <Typography variant="caption" sx={{ color: "#1e40af", fontWeight: "bold", display: "block", mb: 1 }}>
                💬 CLIENT FEEDBACK
              </Typography>
              <Typography variant="body2">
                {task.clientRemarks || "No client feedback provided."}
              </Typography>
            </Box>
          </Grid>

          {/* Description */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight="bold" sx={{ mt: 2 }}>
              ADMIN INSTRUCTIONS / DESCRIPTION
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: "#f9fafb", borderRadius: 2 }}>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {task.description || "No instructions provided."}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default ViewReportTask;
